/** Campi riusati dai form: fornitore dal VendorHub, owner (utente del portale o nome libero), voci di lista. */
import { AutoComplete, Select } from "antd";
import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import type { FornitoreBreveDto, Lista, ListeDto, OwnerDto, UtenteBreveDto } from "@qa/shared";
import { api } from "../api/client";

export function useListe() {
  return useQuery({ queryKey: ["liste"], queryFn: () => api.get<ListeDto>("/liste"), staleTime: 300_000 });
}
/** Voci attive di una lista (più il valore corrente, anche se disattivato, per non perderlo). */
export function useVoci(lista: Lista, corrente?: string | null) {
  const l = useListe();
  return useMemo(() => {
    const v = (l.data?.voci ?? []).filter((x) => x.lista === lista && x.attiva).sort((a, b) => a.ordine - b.ordine).map((x) => x.valore);
    if (corrente && !v.includes(corrente)) v.push(corrente);
    return v;
  }, [l.data, lista, corrente]);
}

export function SelectLista({ lista, value, onChange, placeholder, style }: { lista: Lista; value?: string | null; onChange?: (v: string | null) => void; placeholder?: string; style?: React.CSSProperties }) {
  const voci = useVoci(lista, value);
  return <Select allowClear value={value ?? undefined} onChange={(v) => onChange?.(v ?? null)} placeholder={placeholder} style={{ width: "100%", ...style }} options={voci.map((v) => ({ value: v, label: v }))} />;
}

function useRitardato(v: string, ms = 250) {
  const [d, setD] = useState(v);
  useEffect(() => {
    const h = setTimeout(() => setD(v), ms);
    return () => clearTimeout(h);
  }, [v, ms]);
  return d;
}

/** Fornitore dal VendorHub (sola lettura): ricerca per nome o codice. */
export function FornitoreSelect({ value, iniziale, onChange, disabled }: { value?: string | null; iniziale?: FornitoreBreveDto | null; onChange?: (id: string | null, f: FornitoreBreveDto | null) => void; disabled?: boolean }) {
  const { t } = useTranslation();
  const [q, setQ] = useState("");
  const qd = useRitardato(q.trim());
  const r = useQuery({ queryKey: ["fornitori", qd], queryFn: () => api.get<FornitoreBreveDto[]>("/fornitori", { q: qd || undefined, limit: 20 }), staleTime: 60_000, enabled: !disabled });
  const [scelto, setScelto] = useState<FornitoreBreveDto | null>(iniziale ?? null);
  useEffect(() => setScelto(iniziale ?? null), [iniziale]);
  const opzioni = [...(scelto && !(r.data ?? []).some((f) => f.id === scelto.id) ? [scelto] : []), ...(r.data ?? [])];
  return (
    <Select
      showSearch
      allowClear
      disabled={disabled}
      value={value ?? undefined}
      placeholder={t("redflag.scheda.fornitoreCerca")}
      filterOption={false}
      onSearch={setQ}
      loading={r.isFetching}
      onChange={(id?: string) => {
        const f = opzioni.find((x) => x.id === id) ?? null;
        setScelto(f);
        onChange?.(id ?? null, f);
      }}
      style={{ width: "100%" }}
      options={opzioni.map((f) => ({ value: f.id, label: `${f.nome}${f.codice ? ` · ${f.codice}` : ""}${f.paese ? ` · ${f.paese}` : ""}` }))}
    />
  );
}

export function useUtenti() {
  return useQuery({ queryKey: ["utenti"], queryFn: () => api.get<UtenteBreveDto[]>("/utenti"), staleTime: 300_000 });
}

/** Owner: un utente del portale (proposto mentre scrivi) o un nome libero. */
export function OwnerInput({ value, onChange, placeholder, disabled }: { value?: Partial<OwnerDto> | null; onChange?: (o: OwnerDto) => void; placeholder?: string; disabled?: boolean }) {
  const u = useUtenti();
  const nome = value?.nome ?? "";
  const opzioni = (u.data ?? []).filter((x) => !nome || x.nome.toLowerCase().includes(nome.toLowerCase())).map((x) => ({ value: x.id, label: x.nome }));
  return (
    <AutoComplete
      disabled={disabled}
      value={nome}
      options={opzioni}
      placeholder={placeholder}
      style={{ width: "100%" }}
      onSelect={(id: string) => {
        const x = u.data?.find((y) => y.id === id);
        onChange?.({ nome: x?.nome ?? null, utenteId: x?.id ?? null });
      }}
      onChange={(v: string) => {
        const x = u.data?.find((y) => y.id === v);
        if (x) return;
        onChange?.({ nome: v || null, utenteId: null });
      }}
    />
  );
}
